export type OverviewPeriod = "programme_year" | "this_term" | "last_term";
export type SurveyRole = "all" | "teaching" | "support" | "leaders" | "governors";
export type SurveyWave = "start" | "3m" | "6m" | "12m";
export const SURVEY_WAVE_LABELS: Record<SurveyWave, string> = {
  start: "Start", "3m": "3 months", "6m": "6 months", "12m": "12 months",
};

/** Published aggregate values only; null never means zero. */
export interface OverviewMetric {
  value: number | null;
  status: "ready" | "empty" | "suppressed" | "pending";
  numerator?: number;
  denominator?: number;
  unit?: string;
  suppression?: "minimum" | "complement" | "secondary";
}
export interface OverviewQuery {
  period: OverviewPeriod;
  department: string;
  initiativeId?: string;
}
export interface AppliedOverviewFilters extends Omit<OverviewQuery, "initiativeId"> {
  role: SurveyRole;
  start: string;
  end: string;
  academicYear: string;
  label: string;
  legacyRange: string | null;
  initiativeId: string | null;
}
export interface Initiative {
  id: string;
  scope: "school" | "group";
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  measures: string[];
}
export interface InitiativeInput {
  name: string;
  description: string | null;
  start: string;
  end: string;
  measures: string[];
}
export interface OverviewDepartment {
  id: string;
  label: string;
  staff: OverviewMetric;
  core: OverviewMetric;
  workbook: OverviewMetric;
  hours: OverviewMetric;
  status: string;
}
export interface SurveyWaveSummary {
  wave: SurveyWave;
  count: OverviewMetric;
  rate: OverviewMetric | null;
  medianDate: string | null;
  medianTimeBand: number | null;
  rateAvailable: boolean;
  rateMessage: string | null;
  principles: Array<{ id: string; value: OverviewMetric; statements: Array<{ id: string; value: OverviewMetric }> }>;
  fourPrinciples: OverviewMetric;
  judgement: OverviewMetric;
  weeklyUse: OverviewMetric;
  timeSaved: OverviewMetric;
  weeklyBands: Array<{ id: string; value: OverviewMetric }>;
  timeBands: Array<{ id: string; value: OverviewMetric }>;
  scenarios: Array<{ id: string; value: OverviewMetric }>;
  school: Array<{ id: string; value: OverviewMetric }>;
  uses: Array<{ id: string; value: OverviewMetric }>;
  judgementReason: string | null;
  comparisonNote: string;
}
export interface OverviewSurvey {
  latestWave: SurveyWave | null;
  tiles: Array<{ id: "judgement" | "fourPrinciples" | "weeklyUse" | "timeSaved"; start: OverviewMetric; latest: OverviewMetric | null; change: number | null }>;
  role: SurveyRole;
  waves: SurveyWaveSummary[];
  emptyMessage: string;
}
interface OverviewBase {
  filters: AppliedOverviewFilters;
  journey: { stage: string | null; standardAchieved: boolean | null; leadersEnrolled: number | null; advancedCertified: number | null; message: string };
  readiness: {
    coreCertificates: OverviewMetric;
    coreThisTerm: OverviewMetric;
    advancedCertificates: OverviewMetric;
    workbookEngagement: OverviewMetric;
    workbookTrend: { priorPeriod: string; unit: string; value: number | null };
    hoursRedeployed: { total: OverviewMetric; inferred: boolean; perStaffMember: OverviewMetric };
  };
  priorities: Array<{ id: string; label: string; value: OverviewMetric | null; departments?: Array<{ label: string; value: OverviewMetric }> }>;
}
export interface FullOverview extends OverviewBase {
  practice: {
    surveyMarkers: Array<{ wave: SurveyWave; date: string }>;
    initiativeWindows: Array<{ id: string; start: string; end: string }>;
    workbooks: Array<{ key: "lwai_core" | "lwai_advanced"; progress: OverviewMetric; started: OverviewMetric; inProgress: OverviewMetric; complete: OverviewMetric }>;
    modules: Array<{ id: string; title: string; pathway: "core" | "advanced" | null; completion: OverviewMetric }>;
    monthly: Array<{ month: string; teaching: OverviewMetric; support: OverviewMetric }>;
    courseCompletion: Array<{ month: string; core: OverviewMetric; advanced: OverviewMetric }>;
  };
  useCases: {
    rows: UseCaseRow[];
    top3: UseCaseRow[];
    leastTried: UseCaseRow[];
    suggestion: string;
    total: OverviewMetric;
    legend: string[];
    note: string;
  };
  checkin: {
    statements: Array<{ id: string; label: string; direction: string; start: OverviewMetric; end: OverviewMetric; change: number | null }>;
    headline: OverviewMetric;
    averageShare: OverviewMetric;
    matchedClasses: OverviewMetric;
    returns: Array<{ key: "lwai_core" | "lwai_advanced"; eligible: OverviewMetric; start: OverviewMetric; matched: OverviewMetric }>;
    emptyMessage: string;
  };
  survey: OverviewSurvey;
  departments: OverviewDepartment[];
  initiatives: Initiative[];
  initiativeComparison: {
    initiative: Initiative;
    label: string;
    measures: Array<{ measure: string; label: string; start: OverviewMetric | null; end: OverviewMetric | null; change: number | null; series: Array<{ date: string; value: OverviewMetric | null }> }>;
    surveyComparisons: Array<{ id: string; measure: string; unit: string; before: OverviewMetric; after: OverviewMetric; beforeWave: SurveyWave; afterWave: SurveyWave; beforeDate: string; afterDate: string; change: number | null }>;
  } | null;
  ai: { enabled: boolean; cached: Record<string, unknown> };
  meta: {
    accessMode: "full";
    group?: { id: string; name: string; readOnly: true; canExportCsv: boolean; canExportPdf: boolean };
    schoolName: string;
    publicationDay: string;
    publicationId: string;
    departmentOptions: Array<{ id: string; label: string }>;
    windowState: "ready" | "initiative_window_pending" | "initiative_not_started";
    windowPendingMessage: string | null;
  };
}
export interface UseCaseRow {
  id: string;
  label: string;
  teaching: OverviewMetric;
  support: OverviewMetric;
  total: OverviewMetric;
}
export interface TrialOverview extends OverviewBase {
  meta: { accessMode: "trial_preview"; publicationId: string; publicationDay: string; paidPlanMessage: string };
}
export type SchoolOverview = FullOverview | TrialOverview;
export const isFullOverview = (value: SchoolOverview): value is FullOverview => value.meta.accessMode === "full";
